import React, { useState } from "react";
import { format } from "date-fns";
import { EmptyState } from "@/components/consultation/EmptyState";
import { ExpandedCardDialog } from "@/components/consultation/dialogs/ExpandedCardDialog";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import { usePastPrescriptions } from "@/hooks/useConsultation";
import type { PastPrescription } from "@/types/appointments";

interface PastPrescriptionsCardProps {
  appointmentId: string;
}

const PrescriptionTable = ({
  prescription,
}: {
  prescription: PastPrescription;
}) => (
  <div className="rounded-xl border border-border/60 overflow-hidden bg-card">
    {/* Prescription header */}
    <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/30 border-b border-border/60">
      <span className="inline-flex items-center rounded-full border border-border/60 bg-muted/50 px-2 py-0.5 z-caption tabular-nums text-muted-foreground">
        {format(new Date(prescription.created_at), "dd MMM yyyy")}
      </span>
      <span className="inline-flex items-center rounded-full border border-border/60 bg-muted/50 px-2 py-0.5 z-caption text-muted-foreground">
        {prescription.doctor_name}
      </span>
      <span className="inline-flex items-center rounded-full border border-border/60 bg-muted/50 px-2 py-0.5 z-caption tabular-nums text-muted-foreground ml-auto">
        {prescription.zarity_appointment_id}
      </span>
    </div>

    {/* Table */}
    <table className="w-full text-left">
      <thead className="bg-muted/20 border-b border-border/60">
        <tr>
          {["Medicine Name", "Qty", "Frequency", "Duration", "Advice"].map(
            (h) => (
              <th
                key={h}
                scope="col"
                className="px-3 py-1.5 z-eyebrow text-muted-foreground"
              >
                {h}
              </th>
            )
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-border/40">
        {prescription.prescription_items.map((item) => (
          <tr key={item.id} className="hover:bg-muted/20 transition-colors">
            <td className="px-3 py-2">
              <p className="z-secondary font-medium text-foreground">
                {item.medicine.brand_name}
              </p>
              <p className="z-hint text-muted-foreground mt-0.5 line-clamp-2">
                {item.medicine.product_details?.product_name}
              </p>
            </td>
            <td className="px-3 py-2 z-secondary text-muted-foreground whitespace-nowrap tabular-nums">
              {item.quantity}
            </td>
            <td className="px-3 py-2 z-secondary text-muted-foreground">
              {item.medicine_frequency.text}
            </td>
            <td className="px-3 py-2 z-secondary text-muted-foreground whitespace-nowrap">
              {item.medicine_duration.text}
            </td>
            <td className="px-3 py-2 z-secondary text-muted-foreground">
              {item.medicine_advice || "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PrescriptionList = ({
  prescriptions,
  isLoading,
}: {
  prescriptions: PastPrescription[];
  isLoading: boolean;
}) => {
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-4 bg-muted rounded-xl w-3/4 mx-auto" />
        <div className="h-4 bg-muted rounded-xl w-1/2 mx-auto" />
      </div>
    );
  }

  if (!prescriptions || prescriptions.length === 0) {
    return <EmptyState title="No past prescriptions found" variant="compact" />;
  }

  const sorted = [...prescriptions].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <div className="flex flex-col gap-3">
      {sorted.map((prescription) => (
        <PrescriptionTable key={prescription.id} prescription={prescription} />
      ))}
    </div>
  );
};

export const PastPrescriptionsCard = ({
  appointmentId,
}: PastPrescriptionsCardProps) => {
  const { data: pastPrescriptions = [], isLoading } =
    usePastPrescriptions(appointmentId);
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <Widget grow className="h-full">
        <WidgetHeader
          title="Past Zarity Prescriptions"
          hasInfo
          onMaximize={() => setIsExpanded(true)}
        />
        <WidgetContent className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
          <PrescriptionList
            prescriptions={pastPrescriptions}
            isLoading={isLoading}
          />
        </WidgetContent>
      </Widget>

      <ExpandedCardDialog
        open={isExpanded}
        onOpenChange={setIsExpanded}
        title="Past Zarity Prescriptions"
      >
        <div className="h-full overflow-y-auto scrollbar-thin">
          <PrescriptionList
            prescriptions={pastPrescriptions}
            isLoading={isLoading}
          />
        </div>
      </ExpandedCardDialog>
    </>
  );
};
